const React = require('react');
const T = require('prop-types');
const { EditorState, SelectionState, ContentBlock, genKey } = require('draft-js');
const { default: IconButton } = require('@mui/material/IconButton');
const { default: DeleteIcon } = require('@mui/icons-material/Delete');

const { default: Classes } = require('./styles.scss');

const internals = {};

const ImageBlock = (props) => {

    const { block, contentState, blockProps } = props;
    const { getEditorState, onChange } = blockProps;
    const entity = contentState.getEntity(block.getEntityAt(0));
    const { src, alt, width, height } = entity.getData();
    const [hovered, setHovered] = React.useState(false);

    const editorState = getEditorState();
    const blockKey = block.getKey();
    const isSelected = editorState.getSelection().getAnchorKey() === blockKey;
    const showControls = hovered || isSelected;

    const selectBlock = (event) => {

        event.preventDefault();

        const selection = SelectionState.createEmpty(blockKey).merge({
            anchorOffset: 0,
            focusOffset: block.getLength(),
            hasFocus: true
        });

        onChange(EditorState.forceSelection(editorState, selection));
    };

    const handleDelete = (event) => {

        event.preventDefault();
        event.stopPropagation();
        onChange(internals.removeBlock(getEditorState(), blockKey));
    };

    return (
        <div
            className={Classes.imageBlock}
            data-selected={showControls ? 'true' : 'false'}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onMouseDown={selectBlock}
        >
            <img
                src={src}
                alt={alt || ''}
                style={{
                    width: width || 'auto',
                    height: height || 'auto',
                    maxWidth: '100%'
                }}
            />
            {showControls && (
                <div className={Classes.imageControls} onMouseDown={(event) => event.stopPropagation()}>
                    <IconButton
                        size='small'
                        onMouseDown={handleDelete}
                        aria-label='Delete image'
                    >
                        <DeleteIcon fontSize='small' />
                    </IconButton>
                </div>
            )}
        </div>
    );
};

ImageBlock.propTypes = {
    block: T.object.isRequired,
    contentState: T.object.isRequired,
    blockProps: T.shape({
        getEditorState: T.func.isRequired,
        onChange: T.func.isRequired
    }).isRequired
};

internals.removeBlock = (editorState, blockKey) => {

    const content = editorState.getCurrentContent();
    const blockMap = content.getBlockMap();
    const keys = blockMap.keySeq();
    const index = keys.indexOf(blockKey);
    const fallbackKey = keys.get(index + 1) || keys.get(index - 1);

    if (blockMap.size === 1 || !fallbackKey) {
        const empty = new ContentBlock({
            key: genKey(),
            type: 'unstyled',
            text: ''
        });
        const selection = SelectionState.createEmpty(empty.getKey());
        const nextContent = content.merge({
            blockMap: blockMap.clear().set(empty.getKey(), empty),
            selectionAfter: selection
        });

        return EditorState.forceSelection(
            EditorState.push(editorState, nextContent, 'remove-range'),
            selection
        );
    }

    const selection = SelectionState.createEmpty(fallbackKey);
    const nextContent = content.merge({
        blockMap: blockMap.delete(blockKey),
        selectionAfter: selection
    });

    return EditorState.forceSelection(
        EditorState.push(editorState, nextContent, 'remove-range'),
        selection
    );
};

module.exports = ImageBlock;
module.exports.liftImagesToAtomicBlocks = (contentState) => {

    const nextBlocks = [];
    let changed = false;

    contentState.getBlocksAsArray().forEach((block) => {

        if (block.getType() === 'atomic') {
            nextBlocks.push(block);
            return;
        }

        const ranges = [];

        block.findEntityRanges((character) => {

            const entityKey = character.getEntity();

            return entityKey && contentState.getEntity(entityKey).getType() === 'IMAGE';
        }, (start, end) => ranges.push({ start, end }));

        if (!ranges.length) {
            nextBlocks.push(block);
            return;
        }

        changed = true;

        let cursor = 0;

        ranges.forEach(({ start, end }) => {

            if (start > cursor) {
                const before = internals.sliceBlock(block, cursor, start);

                if (before && before.getText().replace(/\u00a0/g, ' ').trim()) {
                    nextBlocks.push(before);
                }
            }

            nextBlocks.push(new ContentBlock({
                key: genKey(),
                type: 'atomic',
                text: ' ',
                characterList: block.getCharacterList().slice(start, start + 1)
            }));

            cursor = end;
        });

        if (cursor < block.getLength()) {
            const after = internals.sliceBlock(block, cursor, block.getLength());

            if (after && after.getText().replace(/\u00a0/g, ' ').trim()) {
                nextBlocks.push(after);
            }
        }
    });

    if (!changed) {
        return contentState;
    }

    let blockMap = contentState.getBlockMap().clear();

    nextBlocks.filter(Boolean).forEach((block) => {

        blockMap = blockMap.set(block.getKey(), block);
    });

    return contentState.merge({ blockMap });
};

module.exports.trimEmptyBlocksBeforeImages = (contentState, expectedCounts) => {

    const blocks = contentState.getBlocksAsArray();
    const nextBlocks = [];
    let imageIndex = 0;

    const isEmptyTextBlock = (block) => block.getType() !== 'atomic'
        && !block.getText().replace(/\u00a0/g, ' ').trim();

    blocks.forEach((block) => {

        if (block.getType() !== 'atomic') {
            nextBlocks.push(block);
            return;
        }

        const allowed = expectedCounts[imageIndex] || 0;
        imageIndex += 1;

        let emptyCount = 0;

        for (let i = nextBlocks.length - 1; i >= 0; i -= 1) {
            if (!isEmptyTextBlock(nextBlocks[i])) {
                break;
            }

            emptyCount += 1;
        }

        while (emptyCount > allowed) {
            nextBlocks.pop();
            emptyCount -= 1;
        }

        nextBlocks.push(block);
    });

    if (nextBlocks.length === blocks.length) {
        return contentState;
    }

    let blockMap = contentState.getBlockMap().clear();

    nextBlocks.forEach((block) => {

        blockMap = blockMap.set(block.getKey(), block);
    });

    return contentState.merge({ blockMap });
};

internals.sliceBlock = (block, start, end) => {

    if (start >= end) {
        return null;
    }

    return new ContentBlock({
        key: genKey(),
        type: block.getType(),
        text: block.getText().slice(start, end),
        characterList: block.getCharacterList().slice(start, end),
        data: block.getData()
    });
};
